import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtUSD, num } from "@/lib/format";
import { toast } from "sonner";
import { Save, Crown } from "lucide-react";

const emptyJob = () => ({
  title: "", employer: "", rate: "", weekly: "36", shiftDiff: "0", otPerMonth: "0",
  signOn: "0", annualBonus: "0", retirement: "0", insurance: "0", parking: "0",
  commuteCost: "0", pto: "0", commuteTime: "0", shift: "Days", employment: "Full-time",
});

function compute(j) {
  const rate = num(j.rate), weekly = num(j.weekly);
  const base = rate * weekly * 52;
  const diff = num(j.shiftDiff) * weekly * 52;
  const ot = rate * 1.5 * num(j.otPerMonth) * 12;
  const bonus = num(j.annualBonus) + num(j.signOn);
  const retirement = base * (num(j.retirement) / 100);
  const commuting = num(j.commuteCost) * 12 + num(j.parking);
  const insurance = num(j.insurance);
  const adjusted = base + diff + ot + bonus + retirement - commuting - insurance;
  return { base, diff, ot, bonus, retirement, commuting, insurance, adjusted };
}

const JF = ({ label, id, job, setJob, jobKey, placeholder }) => (
  <div>
    <Label htmlFor={`${jobKey}-${id}`} className="text-xs text-slate-500">{label}</Label>
    <Input id={`${jobKey}-${id}`} data-testid={`${jobKey}-${id}`} type={["title", "employer"].includes(id) ? "text" : "number"}
      inputMode={["title", "employer"].includes(id) ? "text" : "decimal"} value={job[id]}
      onChange={(e) => setJob((p) => ({ ...p, [id]: e.target.value }))} className="mt-1 rounded-xl" placeholder={placeholder ?? "0"} />
  </div>
);

const JobForm = ({ jobKey, job, setJob, label }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
    <h3 className="font-heading text-lg font-bold text-[#0F2747]">{label}</h3>
    <div className="mt-4 space-y-3">
      <JF label="Job title" id="title" job={job} setJob={setJob} jobKey={jobKey} placeholder="ICU RN" />
      <JF label="Employer (optional)" id="employer" job={job} setJob={setJob} jobKey={jobKey} placeholder="Metro Health" />
      <div className="grid grid-cols-2 gap-3">
        <JF label="Hourly rate ($)" id="rate" job={job} setJob={setJob} jobKey={jobKey} />
        <JF label="Weekly hours" id="weekly" job={job} setJob={setJob} jobKey={jobKey} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <JF label="Shift differential ($/hr)" id="shiftDiff" job={job} setJob={setJob} jobKey={jobKey} />
        <JF label="OT hours / month" id="otPerMonth" job={job} setJob={setJob} jobKey={jobKey} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <JF label="Sign-on bonus ($)" id="signOn" job={job} setJob={setJob} jobKey={jobKey} />
        <JF label="Annual bonus ($)" id="annualBonus" job={job} setJob={setJob} jobKey={jobKey} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <JF label="Retirement match (%)" id="retirement" job={job} setJob={setJob} jobKey={jobKey} />
        <JF label="Insurance cost/yr ($)" id="insurance" job={job} setJob={setJob} jobKey={jobKey} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <JF label="Annual parking ($)" id="parking" job={job} setJob={setJob} jobKey={jobKey} />
        <JF label="Commute cost/mo ($)" id="commuteCost" job={job} setJob={setJob} jobKey={jobKey} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <JF label="PTO hours / year" id="pto" job={job} setJob={setJob} jobKey={jobKey} />
        <JF label="Commute (minutes)" id="commuteTime" job={job} setJob={setJob} jobKey={jobKey} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Shift</Label>
          <Select value={job.shift} onValueChange={(v) => setJob((p) => ({ ...p, shift: v }))}>
            <SelectTrigger data-testid={`${jobKey}-shift`} className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Days">Days</SelectItem><SelectItem value="Nights">Nights</SelectItem><SelectItem value="Rotating">Rotating</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Employment type</Label>
          <Select value={job.employment} onValueChange={(v) => setJob((p) => ({ ...p, employment: v }))}>
            <SelectTrigger data-testid={`${jobKey}-employment`} className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Full-time", "Part-time", "PRN", "Contract", "Travel"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  </div>
);

const Row = ({ label, a, b, money = true }) => (
  <div className="grid grid-cols-3 gap-2 border-b border-slate-100 py-2 text-sm last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-semibold text-[#0F2747]">{money ? fmtUSD(a) : a}</span>
    <span className="text-right font-semibold text-[#0F2747]">{money ? fmtUSD(b) : b}</span>
  </div>
);

export default function JobComparison() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.subscription?.plan === "pro" && user?.subscription?.status === "active";
  const [jobA, setJobA] = useState(emptyJob());
  const [jobB, setJobB] = useState(emptyJob());
  const [quota, setQuota] = useState(null);

  useEffect(() => { api.get("/job-comparisons/quota").then((r) => setQuota(r.data)).catch(() => {}); }, []);

  const ra = useMemo(() => compute(jobA), [jobA]);
  const rb = useMemo(() => compute(jobB), [jobB]);
  const diff = Math.abs(ra.adjusted - rb.adjusted);

  const save = async () => {
    if (!isPro && quota && !quota.unlimited && quota.remaining <= 0) {
      toast.error("Free plan includes 1 saved comparison per month. Upgrade to Pro for unlimited.");
      navigate("/upgrade"); return;
    }
    try {
      await api.post("/job-comparisons", {
        name: `${jobA.title || "Job A"} vs ${jobB.title || "Job B"}`,
        job_a: jobA, job_b: jobB, results: { a: ra, b: rb },
      });
      toast.success("Comparison saved!");
      api.get("/job-comparisons/quota").then((r) => setQuota(r.data));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Job Offer Comparison</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#0F2747]">Compare two nursing offers</h1>
        {quota && !quota.unlimited && (
          <p className="mt-1 text-sm text-slate-500">Free plan: {quota.remaining} saved comparison remaining this month.</p>
        )}
      </div>

      <div className="grid gap-5 md:grid-cols-2" data-testid="offer-compare-matrix">
        <JobForm jobKey="jobA" job={jobA} setJob={setJobA} label="Job A" />
        <JobForm jobKey="jobB" job={jobB} setJob={setJobB} label="Job B" />
      </div>

      {/* Results */}
      <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm" data-testid="comparison-results">
        <div className="grid grid-cols-3 gap-2 pb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
          <span></span><span className="text-right">{jobA.title || "Job A"}</span><span className="text-right">{jobB.title || "Job B"}</span>
        </div>
        <Row label="Annual base salary" a={ra.base} b={rb.base} />
        <Row label="Differential income" a={ra.diff} b={rb.diff} />
        <Row label="Overtime income" a={ra.ot} b={rb.ot} />
        <Row label="Bonuses (incl. sign-on)" a={ra.bonus} b={rb.bonus} />
        <Row label="Retirement match value" a={ra.retirement} b={rb.retirement} />
        <Row label="Commuting costs" a={-ra.commuting} b={-rb.commuting} />
        <Row label="Insurance cost" a={-ra.insurance} b={-rb.insurance} />
        <Row label="Shift" a={jobA.shift} b={jobB.shift} money={false} />
        <Row label="Employment type" a={jobA.employment} b={jobB.employment} money={false} />
        <Row label="PTO hours/yr" a={num(jobA.pto)} b={num(jobB.pto)} money={false} />
        <Row label="Commute (min)" a={num(jobA.commuteTime)} b={num(jobB.commuteTime)} money={false} />

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl bg-[#EAF4FA] p-4">
            <p className="text-xs text-slate-500">{jobA.title || "Job A"} adjusted comp</p>
            <p className="font-heading text-2xl font-bold text-[#0F2747]" data-testid="job-a-total">{fmtUSD(ra.adjusted)}</p>
          </div>
          <div className="rounded-2xl bg-[#EAF4FA] p-4">
            <p className="text-xs text-slate-500">{jobB.title || "Job B"} adjusted comp</p>
            <p className="font-heading text-2xl font-bold text-[#0F2747]" data-testid="job-b-total">{fmtUSD(rb.adjusted)}</p>
          </div>
          <div className="rounded-2xl bg-[#0F2747] p-4 text-white">
            <p className="text-xs text-slate-300">Estimated difference</p>
            <p className="font-heading text-2xl font-bold text-[#D6A84B]" data-testid="job-diff">{fmtUSD(diff)}</p>
          </div>
        </div>

        <p className="mt-4 text-sm text-slate-500">
          Compensation is only one part of a job decision. Consider schedule, benefits, commute,
          workload, career goals, and personal priorities before accepting an offer.
        </p>
      </div>

      <Button onClick={save} data-testid="save-comparison-btn"
        className={`w-full rounded-full py-6 ${isPro ? "bg-[#18A6A6] hover:bg-[#148f8f]" : "bg-[#0F2747] hover:bg-[#16355F]"}`}>
        {isPro ? <><Save className="mr-2 h-4 w-4" /> Save Comparison</> : <><Crown className="mr-2 h-4 w-4 text-[#D6A84B]" /> Save Comparison</>}
      </Button>
    </div>
  );
}
